import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { PALETTES } from "@/lib/eventOptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ImageUpload, { publicImageUrl } from "@/components/ImageUpload";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, BookOpen } from "lucide-react";

export default function Guestbook() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [entries, setEntries] = useState([]);
  const [errState, setErrState] = useState(null);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [photoPath, setPhotoPath] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/public/events/${slug}`)
      .then((r) => setEvent(r.data))
      .catch((err) => setErrState(err.response?.status === 403 ? "locked" : "notfound"));
    api.get(`/public/events/${slug}/guestbook`).then((r) => setEntries(r.data || [])).catch(() => {});
  }, [slug]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Please add your name"); return; }
    if (!message.trim() && !photoPath) { toast.error("Leave a note or a photo"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/public/events/${slug}/guestbook`, {
        name: name.trim(), message: message.trim(), photo_path: photoPath,
      });
      setEntries((es) => [data, ...es]);
      setName(""); setMessage(""); setPhotoPath(null);
      toast.success("Added to the memory wall");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not post");
    } finally { setSaving(false); }
  };

  if (errState) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FBF9F4] text-neutral-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="text-center">
        <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl font-light">
          {errState === "locked" ? "Memory wall not open yet" : "Not found"}
        </div>
        <p className="mt-3">This guestbook isn&apos;t available.</p>
        <Link to="/" className="mt-6 inline-block text-xs tracking-[0.25em] uppercase underline underline-offset-4">Back to Soirée</Link>
      </div>
    </div>;
  }
  if (!event) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;

  if (!event.enable_guestbook) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FBF9F4] text-neutral-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="text-center">
        <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl font-light">No memory wall</div>
        <p className="mt-3">The host hasn&apos;t enabled a guestbook for this event.</p>
        <Link to={`/i/${slug}`} className="mt-6 inline-block text-xs tracking-[0.25em] uppercase underline underline-offset-4">Back to invite</Link>
      </div>
    </div>;
  }

  const palette = PALETTES.find((p) => p.name === event.palette) || PALETTES[0];

  return (
    <div className="min-h-screen" style={{ background: palette.bg, color: palette.primary, fontFamily: "'Manrope', sans-serif" }}>
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-10 text-center">
        <Link to={`/i/${slug}`} className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase opacity-70 hover:opacity-100" data-testid="back-to-invite">
          <ArrowLeft size={12} /> Back to invite
        </Link>
        <div className="mt-8 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase opacity-70">
          <BookOpen size={12} /> Memory wall
        </div>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="mt-4 text-5xl sm:text-6xl font-light tracking-tight">
          {event.title}
        </h1>
        <p className="mt-4 text-sm opacity-80">Leave a note, a wish, or a photo to mark the occasion.</p>
      </section>

      <section className="max-w-2xl mx-auto px-6 lg:px-10 pb-12">
        <div className="border bg-white/70 p-6 sm:p-8 space-y-4" style={{ borderColor: `${palette.primary}22` }} data-testid="guestbook-form">
          <div>
            <Label className="text-[10px] tracking-[0.25em] uppercase">Your name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="gb-name" className="rounded-sm mt-1" placeholder="How should we sign this?" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.25em] uppercase">Message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="gb-message" rows={4} className="rounded-sm mt-1" placeholder="A wish, a memory, a toast…" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.25em] uppercase">Photo (optional)</Label>
            <div className="mt-2">
              <ImageUpload value={photoPath} onChange={setPhotoPath} label="Add a photo" publicSlug={slug} dataTestId="gb-photo" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={saving} data-testid="gb-submit" className="rounded-sm" style={{ background: palette.primary, color: "white" }}>
              {saving ? "Posting…" : "Sign the wall"}
            </Button>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-24">
        <div className="text-[10px] tracking-[0.3em] uppercase opacity-70 mb-4 text-center">{entries.length} {entries.length === 1 ? "note" : "notes"}</div>
        {entries.length === 0 ? (
          <div className="text-center opacity-60 py-10 text-sm">Be the first to leave a memory.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="gb-entries">
            {entries.map((g) => (
              <div key={g.id} className="border bg-white/60 p-5" style={{ borderColor: `${palette.primary}22` }} data-testid={`gb-entry-${g.id}`}>
                {g.photo_path && (
                  <div className="mb-3 -mx-5 -mt-5">
                    <img src={publicImageUrl(g.photo_path)} alt="" className="w-full h-56 object-cover" />
                  </div>
                )}
                <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl">{g.name}</div>
                {g.message && <p className="mt-2 text-sm opacity-90 leading-relaxed whitespace-pre-wrap">{g.message}</p>}
                <div className="mt-3 text-[10px] tracking-[0.25em] uppercase opacity-60">
                  {(() => { try { return format(new Date(g.created_at), "MMM d, yyyy · p"); } catch { return ""; } })()}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
